import {DomainAncestor, entityConfig, fieldInfo} from "core/common/domain/DomainAncestor";
import {observable} from "mobx";
import {EntityConfig, ID} from "core/metamodel/metaConstant";
import {LovItem} from "core/metamodel/mdEntity";
import {lovLabel, lovParam, lovValue} from "core/form/formUtils";
import partyService from "party/partyService";
import mdEntityService from "core/metamodel/mdEntityService";
import {PartyCreditEntry} from "accounting/accountingDomain";
import {Price} from "product/productDomain";
import {SelectableHolder} from "core/common/domain/commonDomain";
import minBy = require("lodash/minBy");

export const PARTYROLE = {
  CUSTOMER_ROLE: 1,
  CONTACT_ROLE: 2,
  SALESREP_ROLE: 3,
  USER_ROLE: 4,
  PRICE_GROUP_ROLE: 5,
  OWNER_ROLE: 6,
  EMAIL_INVOICE: 7,
  EMAIL_NOTIFICATION: 8,
  EMAIL_REGISTRATION: 9,
  OPERATOR: 10,
  SUPPLIER_ROLE: 11,
  EMPLOYEE: 14,
  PACKAGING: 24,
  HAULER: 29,
  EMPLOYER: 30,
  DRIVER: 31
};


// COUNTRIES
export enum COUNTRY {
  CZ = "CZ",
  SK = "SK",
  HU = "HU",
  RO = "RO",
}

export enum PARTYCATEGORY {
  PE = "PE",
  CO = "CO",
  EN = "EN"
}

export enum ADDRESSTYPE {
  PERMANENT = "PERMANENT",
  CORRESPONDENT = "CORRESPONDENT",
  DELIVERY = "DELIVERY",
  OTHER = "OTHER",
}

export enum PARTYCONTACTCATEGORY {
  PHONE = "PHONE",
  EMAIL = "EMAIL",
  WEB = "WEB"
}

export enum PARTYFEATURETYPE {
  INVOICE_DATA = "INVOICE_DATA",
  VOLVO_LOGINS = "VOLVO_LOGINS",
  AUTONEUM_DATA = "AUTONEUM_DATA"
}

export enum ADDRESSPLACETYPE {
  EXPEDITION = "EXPEDITION",
  INCOMING = "INCOMING"
}

export enum PARTYNOTETYPE {
  VEHICLE_NOTIFICATION = "VEHICLE_NOTIFICATION",
  INVOICE = "INVOICE"
}

export interface SelectableHolderWithPartyLov<V> extends SelectableHolder<V> {
  partyLov: LovItem<ID>;
}

@entityConfig()
export class PartyRole extends DomainAncestor {

  @observable partyRoleId: number;
  @observable roleType: LovItem<number>;
  @observable party: LovItem;
  @observable validFrom: string|Date;
  @observable validTo: string|Date;


  constructor(json: any) {
    super(json);
  }

  public getId(): ID {
    return this.partyRoleId;
  }

  public setId(id: ID): void {
    this.partyRoleId = <any>id;
  }

}


@entityConfig()
export class AddressPlace extends DomainAncestor {

  @observable addressPlaceId: number;
  @observable address: LovItem;
  @observable party: LovItem;
  @observable placeType: LovItem<ADDRESSPLACETYPE>;
  @observable name: string;

  constructor(json: any) {
    super(json);
  }

  public getId(): ID {
    return this.addressPlaceId;
  }

  public setId(id: ID): void {
    this.addressPlaceId = <number>id;
  }

}

@entityConfig()
export class PartyInfo extends DomainAncestor {

  @observable id: string;
  @observable vatId: string;
  @observable permanentAddress: Address;
  @observable defaultBankAccount: PartyBankAccount;

  constructor(json: any) {
    super(json);
    this.permanentAddress = new Address(json["permanentAddress"]);
    if (!!json["defaultBankAccount"]) {
      this.defaultBankAccount = new PartyBankAccount(json["defaultBankAccount"]);
    }
  }

}

@entityConfig()
export class Address extends DomainAncestor {

  static addressLineFromItems(street, city, zip, country) {
    return ((street) ? street : '')
      + ((city || zip) ? ',' : '')
      + ((city) ? ' ' + city : '')
      + ((zip) ? ' ' + zip : '')
      + ((country) ? ', ' + country.label : '');
  }




  @observable addressId: number;
  @observable party: LovItem;
  @observable addressType: LovItem;

  @observable name: string;
  @observable street: string;
  @observable toHands: string;
  @observable descriptiveNumber: string;
  @observable orientationalNumber: string;
  @observable city: string;
  @observable zip: string;
  @observable comment: string;
  @observable country: LovItem;

  @observable administrativeArea: string;
  @observable locationType: string;
  @observable placeId: string;
  @observable locationLat: number;
  @observable locationLng: number;

  @observable ftlEscalationMinutes: string;
  @observable ltlEscalationMinutes: string;

  @observable defaultDisponentParty: LovItem;


  constructor(json: any) {
    super(json);
  }

  public getId(): ID {
    return this.addressId;
  }

  public setId(id: ID): void {
    this.addressId = <any>id;
  }


  public fullStreet() {
    return [
      (!this.street) ? this.city : this.street,
      [this.descriptiveNumber, this.orientationalNumber].filter(s => s).join("/")
    ].filter(s => s).join(" ");
  }

  public addressLine() {
    let street = this.fullStreet();

    return this.fullStreet()
      + ((this.city || this.zip) ? ',' : '')
      + ((this.city) ? ' ' + this.city : '')
      + ((this.zip) ? ' ' + this.zip : '')
      + ((this.administrativeArea) ? ', ' + this.administrativeArea : '')
      + ((this.country) ? ', ' + this.country.label : '');
  }

}


@entityConfig()
export class PartyContact extends DomainAncestor {

  @observable partyContactId: number;
  @observable contactType: LovItem<number> & {category?: string|number};
  @observable party: LovItem;
  @observable validFrom: string|Date;
  @observable validTo: string|Date;
  @observable orderByType: number;
  @observable value: string;
  @observable comment: string;


  constructor(json: any) {
    super(json);
  }

  public getId(): ID {
    return this.partyContactId;
  }

  public setId(id: ID): void {
    this.partyContactId = <any>id;
  }

}


@entityConfig()
export class PartyNote extends DomainAncestor {

  @observable partyNoteId: number;
  @observable content: string;
  @observable party: LovItem;
  @observable noteType: LovItem;

  constructor(json: any) {
    super(json);
  }

  public getId(): ID {
    return this.partyNoteId;
  }

  public setId(id: ID): void {
    this.partyNoteId = <any>id;
  }

}


@entityConfig(class extends EntityConfig {
  constructor() {
    super("PartyFeature");
  }
  getFormName(historyObject) {
    return historyObject.fullObject.featureType + 'Form';
  }
})
export class PartyFeature extends DomainAncestor {

  @observable partyFeatureId: number;
  @observable featureType: string;
  @observable party: LovItem;
  @observable value1: string;
  @observable value2: string;
  @observable value3: string;
  @observable value4: string;
  @observable value5: string;
  @observable value6: string;
  @observable value7: string;
  @observable value8: string;
  @observable value9: string;
  @observable value10: string;
  @observable value11: string;
  @observable value12: string;

  constructor(json: any) {
    super(json);
  }

  public getId(): ID {
    return this.partyFeatureId;
  }

  public setId(id: ID): void {
    this.partyFeatureId = <any>id;
  }

}


export interface PartyRoleTypeStruct {
  value: number;
  label: string;
  directionFrom: boolean;
  roleId: number;
  roleLabel: string;
}

@entityConfig()
export class PartyRel extends DomainAncestor {

  @observable partyRelId: number;
  @observable relType: LovItem & PartyRoleTypeStruct;
  @observable fromParty: LovItem;
  @observable toParty: LovItem;

  @observable fromPartyOrderByType: number;
  @observable toPartyOrderByType: number;
  @observable description: string;
  @observable comments: string;
  @observable validFrom: string|Date;
  @observable validTo: string|Date;

  constructor(json: any) {
    super(json);
  }

  public getId(): ID {
    return this.partyRelId;
  }

  public setId(id: ID): void {
    this.partyRelId = <any>id;
  }

}




@entityConfig()
export class PartyBankAccount extends DomainAncestor {

  @observable partyBankAccountId: number;
  @observable bank: LovItem;
  @observable party: LovItem;
  @observable accountPrefix: string;
  @observable accountNumber: string;
  @observable iban: string;
  @observable currency: LovItem;
  @observable comment: string;
  @observable validFrom: Date|string;
  @observable validTo: Date|string;
  apiToken: string;
  @observable hasApiToken: boolean;

  constructor(json: any) {
    super(json);
  }

  public getId(): ID {
    return this.partyBankAccountId;
  }

  public setId(id: ID): void {
    this.partyBankAccountId = <any>id;
  }

  public getFullAccount(): string {
    let bankEntity = mdEntityService.getEntity("Bank");
    let bankCode = bankEntity.getLovItemParam(lovValue(this.bank), "bankCode");
    return ((this.accountPrefix)?(this.accountPrefix + "-"):"") + this.accountNumber + ((bankCode)?("/" + bankCode):"");
  }

}




@entityConfig(class extends EntityConfig {
  constructor() {
    super("Party");
  }

  getFormName(historyObject) {
    return (historyObject.fullObject.partyCategory === PARTYCATEGORY.PE) ? "PartyFoForm" : "PartyPoForm";
  }
}, "partyDetail")
export class Party extends DomainAncestor {

  @observable partyId: number;

  @observable fullName: string;
  @observable firstName: string;
  @observable lastName: string;
  @observable titleBefore: string;
  @observable dateOfBirth: string|Date;
  @observable birthNumber: string;
  @observable birthLastName: string;
  @observable salutation: string;
  @observable placeOfBirth: string;
  @observable ico: string;
  @observable dic: string;
  @observable icoDph: string;
  @observable commercialRegister: string;

  @observable nationality: LovItem;
  @observable taxDomicile: LovItem;
  @observable gender: LovItem;
  @observable partyCategory: string;
  @observable defaultSendType: LovItem;
  @observable defaultAttachmentType: LovItem;
  @observable defaultCurrency: LovItem;
  @observable defaultLanguage: LovItem;
  @observable defaultPaymentCond: LovItem;
  @observable legalForm: LovItem;
  @observable naceCode: LovItem;
  @observable marketingSource: LovItem;
  @observable defaultProfitCenter: LovItem;
  @observable vatIdCheckDate: string|Date;
  @observable closeDate: string|Date;
  @observable vatRateOverride: LovItem;
  @observable hrNumber: string;

  @observable @fieldInfo(PartyContact) contacts: PartyContact[];
  @observable @fieldInfo(Address) addresses: Address[];
  @observable @fieldInfo(PartyNote) notes: PartyNote[];
  @observable @fieldInfo(PartyFeature) features: PartyFeature[];
  @observable @fieldInfo(PartyRole) roles: PartyRole[];
  @observable @fieldInfo(PartyBankAccount) bankAccounts: PartyBankAccount[];

  @observable $creditEntries: PartyCreditEntry[];
  @observable $creditSums: Price[];


  constructor(json: Partial<Party>) {
    super(json);

    if (!this.contacts) this.contacts = [];
    if (!this.addresses) this.addresses = [];
    if (!this.notes) this.notes = [];
    if (!this.features) this.features = [];
    if (!this.roles) this.roles = [];
    if (!this.bankAccounts) this.bankAccounts = [];
  }

  public getId(): ID {
    return this.partyId;
  }

  public setId(id: ID): void {
    this.partyId = <any>id;
  }


  public roleName(): string {
    // min protoze party pojmenujeme podle role s nejnizsim roleTypeId
    let minRole: any = minBy<any>(this.roles, role => lovValue(role.roleType));
    return (minRole) ? lovLabel(minRole.roleType) : "Party";
  }


  public partyIcon() {
    // min protoze party pojmenujeme podle role s nejnizsim roleTypeId
    let minRole = minBy<any>(this.roles, role => lovValue(role.roleType));
    return (minRole) ? lovParam(minRole.roleType, "icon") : "user";
  }


  /**
   * bacha pouziva se v default vyrazech ulozenych v DB
   * @param roleType
   * @return {boolean}
   */
  public hasRole(roleType: number) : boolean {
    return this.roles.findIndex(role => lovValue(role.roleType) === roleType) !== -1;
  }

  public hasOneOfRoles(...roleTypes: number[]): boolean {
    return this.roles.findIndex(role => roleTypes.includes(lovValue(role.roleType))) !== -1;
  }

  /**
   *
   * @param roleType
   */
  public addRole(roleType: number): void {
    this.roles.push(partyService.newRole(roleType));
  }


  public partyLabel(): string {
    return this.fullName + ((this.ico) ? ' (' + this.ico + ')' : '');
  }

  public partyLov(): LovItem<number> & {roleTypes?: number[]} {
    return {
      value: this.partyId,
      label: this.partyLabel(),
      roleTypes: this.roles.map(r => r.roleType.value)
    };
  }

  public partyPermanentAddress(): Address {
    if (this.addresses) {
      return this.addresses.find(address => lovValue(address.addressType) === ADDRESSTYPE.PERMANENT);
    } else {
      return null;
    }
  }

  public partyCorrespondentAddress(): Address {
    if (this.addresses) {
      return this.addresses.find(address => lovValue(address.addressType) === ADDRESSTYPE.CORRESPONDENT);
    } else {
      return null;
    }
  }

  public getPartyFeature(featureType: PARTYFEATURETYPE): PartyFeature {
    return this.features.find(f => f.featureType === featureType);
  }


  public getAccountingMatchingId(): string {
    let partyFeature = this.getPartyFeature(PARTYFEATURETYPE.INVOICE_DATA);
    return (partyFeature != null) ? partyFeature.value1 : null;
  }

  public getInvoicingPeriod(): string {
    let partyFeature = this.getPartyFeature(PARTYFEATURETYPE.INVOICE_DATA);
    return (partyFeature != null) ? partyFeature.value4 : null;
  }

  public getInvoicingStartDate(): string {
    let partyFeature = this.getPartyFeature(PARTYFEATURETYPE.INVOICE_DATA);
    return (partyFeature != null) ? partyFeature.value5 : null;
  }


}





// WEBPACK FOOTER //
// ./src/party/partyDomain.ts